import api from "../api";

export interface LibraryCard {
  id: string;
  tenantId: string;
  userId: string;
  membershipId?: string;
  virtualCardNumber: string;
  status: "PENDING" | "PROCESSING" | "READY" | "PICKED_UP" | "EXPIRED";
  generatedAt?: string;
  processingStartedAt?: string;
  readyAt?: string;
  pickupDeadline?: string;
  pickedUpAt?: string;
  latePickupFine?: number;
  finePaidAt?: string;
  notes?: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    metadata?: any;
    profilePicture?: string;
  };
  tenant?: {
    id: string;
    name: string;
  };
  membership?: {
    id: string;
    joinedAt?: string;
    roleRef?: {
      name: string;
    };
    patronType?: {
      id: string;
      name: string;
    };
  };
}

export interface UpdateCardStatusDto {
  cardIds: string[];
  status: LibraryCard["status"];
  notes?: string;
  customPickupDeadline?: string; // ISO date string
}

export const libraryCardsApi = {
  /**
   * Get patron's own library card
   */
  async getMyCard(): Promise<LibraryCard> {
    const response = await api.get("/library-cards/my-card");
    return response.data;
  },

  /**
   * Admin: Get all cards for processing
   */
  async getCardsForProcessing(filters?: {
    status?: LibraryCard["status"];
    search?: string;
  }): Promise<LibraryCard[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.search) params.append("search", filters.search);
    const response = await api.get(`/library-cards/admin/processing?${params.toString()}`);
    return response.data;
  },

  /**
   * Admin: Update card status (bulk)
   */
  async updateCardStatus(data: UpdateCardStatusDto): Promise<{ count: number }> {
    const response = await api.put("/library-cards/admin/status", data);
    return response.data;
  },

  /**
   * Staff: confirm physical card pickup (requires cards.markPickup or cards.process).
   */
  async markPickedUp(cardIds: string[]): Promise<{ count: number }> {
    const response = await api.post("/library-cards/admin/mark-picked-up", { cardIds });
    return response.data;
  },

  /**
   * Admin: Check expired pickups
   */
  async checkExpiredPickups(): Promise<{ message: string; count: number }> {
    const response = await api.post("/library-cards/admin/check-expired");
    return response.data;
  },

  /**
   * Admin: Get card data for printing (includes verification tokens and saved print layout)
   */
  async getPrintData(cardIds?: string[]): Promise<{
    cards: (LibraryCard & { verificationToken: string })[];
    libraryCardPrint: unknown;
  }> {
    const params = new URLSearchParams();
    if (cardIds && cardIds.length > 0) params.append("cardIds", cardIds.join(","));
    const response = await api.get(`/library-cards/admin/print-data?${params.toString()}`);
    return response.data;
  },
};
